import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';
import { tagOneSignalUser, untagOneSignalUser } from '@/lib/onesignal';
import { devLog } from '@/utils/devLog';

// Cache-busting: Add timestamp to prevent cached responses
const nocache = () => `_t=${Date.now()}`;

// Get API URL at runtime - handles both env variable and fallback to origin
const getUrl = () => getApiUrl();

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showTrialOffer, setShowTrialOffer] = useState(false);

  // Check auth status on mount (skip if on auth callback page)
  useEffect(() => {
    // Don't run auth check if we're on the callback page - let the callback handle it
    if (window.location.hash?.includes('session_id=') || 
        window.location.pathname === '/auth/callback') {
      setIsLoading(false);
      return;
    }
    checkAuth();
  }, []);

  // Heartbeat: while the tab is open and authenticated, ping /api/auth/me
  // every 5 minutes so the backend bumps `last_active_at` and the admin
  // "Site Traffic" widget reports users who are passively browsing
  // (e.g., reading a blog post) as active, not just login events.
  useEffect(() => {
    if (!isAuthenticated) return;
    const HEARTBEAT_MS = 5 * 60 * 1000;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        apiFetch('/api/auth/me').catch(() => { /* swallow */ });
      }
    }, HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const checkAuth = async (isRetry = false) => {
    try {
      const { data: userData, error, status } = await apiFetch('/api/auth/me');

      if (!error && userData) {
        setUser(userData);
        setIsAuthenticated(true);
        tagOneSignalUser(userData.user_id);

        // Auto-detect & sync the user's IANA timezone if changed.
        // Used by the streak-saver push (and any future per-user-time
        // logic) so users get notified at 22:00 in their local time
        // instead of falling back to America/Chicago.
        try {
          const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (browserTz && browserTz !== userData.timezone) {
            apiFetch('/api/auth/timezone', {
              method: 'PUT',
              body: { timezone: browserTz },
            }).catch(() => { /* non-blocking */ });
          }
        } catch { /* very old browsers without Intl */ }

        // Apply server-stored preferences to localStorage (so they survive app updates)
        if (userData.preferences) {
          const p = userData.preferences;
          if (p.theme) localStorage.setItem('theme', p.theme);
          if (p.language) localStorage.setItem('defaultLanguage', p.language);
          if (p.opening_screen) localStorage.setItem('clearcomm-opening-screen', p.opening_screen);
          // Apply theme immediately
          if (p.theme) {
            const root = document.documentElement;
            if (p.theme === 'dark') root.classList.add('dark');
            else if (p.theme === 'light') root.classList.remove('dark');
            else if (p.theme === 'system') {
              if (window.matchMedia('(prefers-color-scheme: dark)').matches) root.classList.add('dark');
              else root.classList.remove('dark');
            }
          }
        }
      } else if (status === 401 || status === 403) {
        // Token is genuinely invalid/expired — clear it and log the user out.
        localStorage.removeItem('auth_token');
        setUser(null);
        setIsAuthenticated(false);
      } else {
        // Transient failure (network, 5xx, timeout, parse error). Do NOT
        // log the user out — their JWT is still valid for up to 7 days.
        // Retry once after a short delay before giving up. This handles the
        // common case where a service-worker-triggered reload races with an
        // in-flight long request (e.g., ElevenLabs medical audio).
        if (!isRetry && localStorage.getItem('auth_token')) {
          console.warn('[Auth] /me transient failure, retrying in 2s. status:', status, 'error:', error);
          setTimeout(() => checkAuth(true), 2000);
          return; // keep isLoading=true so UI doesn't flash logged-out
        }
        console.warn('[Auth] /me check failed after retry, keeping token but marking unauthenticated:', error);
        setIsAuthenticated(false); // don't clobber storage

        // Show a "Restore session" toast so the user knows what's happening
        // and can recover with one tap instead of seeing a silent logout.
        if (localStorage.getItem('auth_token')) {
          toast.error('Connection issue — your session is preserved.', {
            id: 'auth-restore', // de-duped: only one toast at a time
            duration: 10000,
            action: {
              label: 'Refresh',
              onClick: () => {
                toast.dismiss('auth-restore');
                checkAuth(false);
              },
            },
          });
        }
      }
    } catch (error) {
      // Defensive: also a transient failure path. Don't clear token.
      console.error('Auth check failed (kept token):', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user should see trial offer (free user, hasn't dismissed recently)
  const shouldShowTrialOffer = (userData) => {
    if (!userData) return false;
    // Don't show if admin
    if (userData.is_admin) return false;
    // Don't show if already on trial or active subscription
    if (userData.subscription_status === 'trial' || userData.subscription_status === 'active') return false;
    // Check if user dismissed the offer recently (within 24 hours)
    const dismissedAt = localStorage.getItem('trialOfferDismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      if (now - dismissedTime < 24 * 60 * 60 * 1000) return false; // 24 hours
    }
    return true;
  };

  const login = async (email, password) => {
    const { data, error } = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: { email, password }
    });
    
    if (error) {
      throw new Error(error || 'Login failed');
    }
    
    setUser(data.user);
    setIsAuthenticated(true);
    localStorage.setItem('auth_token', data.token);
    tagOneSignalUser(data.user?.user_id);
    
    if (shouldShowTrialOffer(data.user)) {
      setShowTrialOffer(true);
    }

    try {
      const { track } = await import('@/lib/analytics');
      track.login('email');
    } catch { /* ignore */ }
    
    return data;
  };

  const register = async (name, email, password) => {
    const { data, error } = await apiFetch('/api/auth/register', {
      method: 'POST',
      body: { name, email, password }
    });
    
    if (error) {
      throw new Error(error || 'Registration failed');
    }
    
    setUser(data.user);
    setIsAuthenticated(true);
    localStorage.setItem('auth_token', data.token);
    tagOneSignalUser(data.user?.user_id);
    
    if (shouldShowTrialOffer(data.user)) {
      setShowTrialOffer(true);
    }

    try {
      const { track } = await import('@/lib/analytics');
      track.signUp('email');
    } catch { /* ignore */ }
    
    return data;
  };

  const loginWithGoogle = async () => {
    // Prefer the app's OWN Google OAuth client (shows clearcommunicationapp.com
    // on the consent screen) whenever the backend has it enabled. Every
    // caller (Login, Register, prompts) funnels through here so no entry
    // point can fall back to the Emergent-managed flow by accident.
    try {
      const r = await fetch(`${window.location.origin}/api/auth/google/status`);
      const j = await r.json();
      if (j.enabled && j.configured) {
        window.location.href = `${window.location.origin}/api/auth/google/login?redirect=/`;
        return;
      }
    } catch { /* status unreachable — fall through to Emergent-managed */ }
    // Use window.location.origin so OAuth always returns to the EXACT
    // domain the user is on (preview, localhost, apex, etc.).
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    //
    // Note: apex (`clearcommunicationapp.com`) is the canonical host.
    // Cloudflare 308-redirects `www.*` → apex, so users who landed on
    // www get their OAuth callback redirected back to apex. The S82
    // apex→www override was removed once the canonical was confirmed
    // to be apex — it added an unnecessary hop that some browsers
    // (Opera) used to strip the URL hash on.
    const redirectUrl = window.location.origin + '/auth/callback';

    // Use location.replace to avoid adding to browser history (prevents back-button issues)
    window.location.replace(`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
  };

  const processGoogleCallback = async (sessionId) => {
    // CRITICAL: Use window.location.origin (NOT getUrl/REACT_APP_BACKEND_URL)
    // This ensures same-origin requests in production, avoiding cross-origin
    // blocks on mobile browsers like Samsung Internet. The API is on the same
    // domain via ingress.
    //
    // Single-shot, single-read. Google session_ids are one-time-use, so
    // retrying is pointless and a re-read of an already-consumed Response
    // body is the source of "body stream already read" errors users have
    // reported. If anything fails here we surface a clean error and let the
    // user re-authenticate from /login.
    const url = `${window.location.origin}/api/auth/google/session?${nocache()}`;

    devLog('Processing Google callback with session:',
      sessionId?.substring(0, 20) + '...');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store',
          'Pragma': 'no-cache',
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
        signal: controller.signal,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      if (err?.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection and sign in again.');
      }
      throw new Error('Network error: please check your connection and sign in again.');
    }
    clearTimeout(timeoutId);

    // Read the body exactly once. If it was already consumed (e.g. by a
    // service worker), fall back to a clean error rather than crashing.
    let data = null;
    try {
      if (response.bodyUsed) {
        throw new Error('Response already consumed');
      }
      data = await response.json();
    } catch {
      throw new Error('Sign-in could not complete. Please try signing in again.');
    }

    if (!response.ok) {
      const detail = data?.detail || data?.message || `Authentication failed (${response.status})`;
      throw new Error(detail);
    }

    if (!data?.token || !data?.user) {
      throw new Error('Sign-in response was incomplete. Please try again.');
    }

    devLog('Google auth successful, user:', data.user?.email);

    setUser(data.user);
    setIsAuthenticated(true);
    localStorage.setItem('auth_token', data.token);
    tagOneSignalUser(data.user?.user_id);

    if (shouldShowTrialOffer(data.user)) {
      setShowTrialOffer(true);
    }

    return data;
  };

  const logout = async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setUser(null);
    setIsAuthenticated(false);
    setShowTrialOffer(false);
    localStorage.removeItem('auth_token');
    untagOneSignalUser();
  };

  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data: userData, error } = await apiFetch('/api/auth/me');
      if (!error && userData) {
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Refresh user failed:', error);
    }
  }, []);

  const dismissTrialOffer = useCallback(() => {
    setShowTrialOffer(false);
    localStorage.setItem('trialOfferDismissed', Date.now().toString());
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    loginWithGoogle,
    processGoogleCallback,
    logout,
    checkAuth,
    updateUser,
    refreshUser,
    showTrialOffer,
    dismissTrialOffer
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
